import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Info } from 'lucide-react'
import { CheckOutlineIcon, ImageIcon, VideoIcon } from '../Icons'

interface AdFormatSpecificationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function AdFormatSpecificationsDialog({
  open,
  onOpenChange,
}: Readonly<AdFormatSpecificationsDialogProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[840px] w-[90%] p-6 bg-white rounded-lg h-[90%] md:h-fit overflow-auto"
        showCloseButton={false}
      >
        {/* Header with Icon */}
        <div className="flex flex-col gap-4 overflow-auto">
          <div className="flex items-center lg:items-start flex-row lg:flex-col gap-2 lg:gap-4">
            <div className="flex items-center justify-center size-8 lg:size-10 bg-ss-brand-100 rounded-full">
              <Info
                className="size-4 lg:size-5 text-ss-brand-200"
                aria-hidden="true"
              />
            </div>
            <DialogHeader className="gap-0">
              <DialogTitle className="text-base lg:text-lg font-semibold leading-7 text-ss-neutral-950 text-left">
                Ad Format Specifications
              </DialogTitle>
            </DialogHeader>
          </div>

          {/* Content */}
          <div className="flex gap-4 w-full flex-col md:flex-row h-[90%] overflow-auto">
            {/* Image Specifications */}
            <div className="flex-1 p-3 border border-ss-neutral-50 rounded-lg">
              <div className="flex flex-col gap-3">
                {/* Header */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex gap-2.5 items-start w-full">
                    <div className="flex gap-2.5 items-start">
                      <ImageIcon
                        className="w-6 h-6 text-ss-neutral-950"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-3">
                      <div className="flex gap-1 items-center">
                        <h3 className="text-base font-bold leading-[22px] text-ss-neutral-950">
                          Image Specifications
                        </h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                    Requirements for image advertisements
                  </p>
                </div>

                {/* Specifications List */}
                <div className="flex flex-col gap-2.5">
                  {/* Leaderboard Banner */}
                  <div className="pb-2.5 border-b border-ss-neutral-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Leaderboard Banner
                      </p>
                      <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                        728 x 90 pixels • Max 150KB • JPEG, PNG, GIF
                      </p>
                    </div>
                  </div>

                  {/* Medium Rectangle */}
                  <div className="pb-2.5 border-b border-ss-neutral-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Medium Rectangle
                      </p>
                      <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                        300 x 250 pixels • Max 100KB • JPEG, PNG, GIF
                      </p>
                    </div>
                  </div>

                  {/* Half Page Ad */}
                  <div className="pb-2.5 border-b border-ss-neutral-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Half Page Ad
                      </p>
                      <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                        300 x 600 pixels • Max 200KB • JPEG, PNG, GIF
                      </p>
                    </div>
                  </div>

                  {/* Mobile Banner */}
                  <div className="flex flex-col gap-0.5">
                    <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                      Mobile Banner
                    </p>
                    <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                      300 x 250 pixels • Max 100KB • JPEG, PNG, GIF
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Specifications */}
            <div className="flex-1 p-3 border border-ss-neutral-50 rounded-lg">
              <div className="flex flex-col gap-3">
                {/* Header */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex gap-2.5 items-start w-full">
                    <div className="flex gap-2.5 items-start">
                      <VideoIcon
                        className="w-6 h-6 text-ss-neutral-950"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-3">
                      <div className="flex gap-1 items-center">
                        <h3 className="text-base font-bold leading-[22px] text-ss-neutral-950">
                          Video Specifications
                        </h3>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                    Requirements for video advertisements
                  </p>
                </div>

                {/* Specifications List */}
                <div className="flex flex-col gap-2.5">
                  {/* Standard Landscape */}
                  <div className="pb-2.5 border-b border-ss-neutral-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Standard Landscape
                      </p>
                      <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                        1280x720 or 1920x1080 • Max 30MB • MP4, MOV • 15-30s
                      </p>
                    </div>
                  </div>

                  {/* Vertical (Mobile) */}
                  <div className="pb-2.5 border-b border-ss-neutral-50">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Vertical (Mobile)
                      </p>
                      <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                        1080x1920 • Max 30MB • MP4, MOV • 15-30s
                      </p>
                    </div>
                  </div>

                  {/* Technical Requirements */}
                  <div className="p-3 bg-ss-neutral-50 rounded-lg">
                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold leading-4 text-ss-neutral-950 w-full">
                        Technical Requirements
                      </p>
                      <div className="flex gap-3 w-full flex-wrap">
                        {/* Left Column */}
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="flex gap-2 items-center w-full">
                            <CheckOutlineIcon
                              className="w-3 h-3 text-ss-positive-200"
                              aria-hidden="true"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                                Frame Rate: 24-30 fps
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-start w-full">
                            <CheckOutlineIcon
                              className="w-3 h-3 text-ss-positive-200 mt-0.5"
                              aria-hidden="true"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                                Bitrate: ≤ 5 Mbps recommended
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="flex gap-2 items-center w-full">
                            <CheckOutlineIcon
                              className="w-3 h-3 text-ss-positive-200"
                              aria-hidden="true"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                                Resolution: 72 dpi
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 items-center w-full">
                            <CheckOutlineIcon
                              className="w-3 h-3 text-ss-positive-200"
                              aria-hidden="true"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-normal leading-4.5  text-ss-neutral-600 w-full">
                                Color Mode: RGB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-4">
            <Button onClick={() => onOpenChange(false)}>OK</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AdFormatSpecificationsDialog
